
import React, { useState, useMemo } from 'react';
import { Customer, Employee } from '../types';
import { Button } from './Button';

interface CustomerSearchPageProps {
  customers: Customer[];
  onSelectCustomer: (customerId: string) => void;
  currentUser: Employee | null;
}

const UserCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-10 h-10 text-slate-400" {...props}>
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z" clipRule="evenodd" />
  </svg>
);

export const CustomerSearchPage: React.FC<CustomerSearchPageProps> = ({ customers, onSelectCustomer, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) {
      return customers;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return customers.filter(customer =>
      customer.name.toLowerCase().includes(lowerSearchTerm) ||
      customer.email?.toLowerCase().includes(lowerSearchTerm) ||
      customer.phoneNumber?.includes(searchTerm) // Phone numbers might not need toLowerCase
    );
  }, [customers, searchTerm]);

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Customer Directory</h1>
        <p className="text-sm text-slate-600 mb-4">Search for customers by name, email, or phone number.</p>
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-label="Search customers"
        />
      </div>

      {filteredCustomers.length === 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-slate-500">No customers found matching your search criteria.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCustomers.map(customer => (
          <div key={customer.id} className="bg-white p-5 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center space-x-4 mb-3">
              <UserCircleIcon />
              <div>
                <h2 className="text-lg font-semibold text-slate-800">{customer.name}</h2>
                {customer.email && <p className="text-xs text-slate-500 truncate" title={customer.email}>{customer.email}</p>}
                {customer.phoneNumber && <p className="text-xs text-slate-500">{customer.phoneNumber}</p>}
              </div>
            </div>
             <p className="text-xs text-slate-400 mb-3">Member since: {new Date(customer.createdAt).toLocaleDateString()}</p>
            <Button
              variant="secondary"
              onClick={() => onSelectCustomer(customer.id)}
              className="w-full text-sm"
            >
              View Profile
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};